import React, { useState, useRef, useEffect, useContext } from 'react'
import { SkipBack, SkipForward, Pause, Play, Repeat2, Volume2, Share2, X } from 'lucide-react'
import { CantiqueContextAPI } from '../Context/CantiqueContext'
import { getCantiqueAudioUrl, hasCantiqueAudio } from '../utils/cantiqueAudio'
import useOnlineStatus from '../hooks/useOnlineStatus'

const Lector = ({ onClose }) => {
  const { selectedCantique } = useContext(CantiqueContextAPI)
  const online = useOnlineStatus()
  const audioRef = useRef(null)
  const probingDurationRef = useRef(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(0.7)
  const [isRepeat, setIsRepeat] = useState(false)
  const [audioError, setAudioError] = useState(null)

  const audioUrl = getCantiqueAudioUrl(selectedCantique)
  const cantiqueHasAudio = hasCantiqueAudio(selectedCantique)

  const resolveDuration = (audio) => {
    if (!audio) return 0
    const raw = audio.duration
    if (Number.isFinite(raw) && raw > 0) return raw
    if (audio.seekable?.length > 0) {
      const end = audio.seekable.end(audio.seekable.length - 1)
      if (Number.isFinite(end) && end > 0) return end
    }
    return 0
  }

  const syncDuration = (audio) => {
    const nextDuration = resolveDuration(audio)
    if (nextDuration > 0) {
      setDuration(nextDuration)
      probingDurationRef.current = false
    }
  }

  const probeDuration = (audio) => {
    if (!audio || probingDurationRef.current) return
    if (resolveDuration(audio) > 0) return

    probingDurationRef.current = true
    const savedTime = audio.currentTime
    const wasPlaying = !audio.paused

    const finishProbe = () => {
      syncDuration(audio)
      audio.currentTime = savedTime
      if (wasPlaying) {
        audio.play().catch(() => {})
      }
      probingDurationRef.current = false
    }

    const onDurationReady = () => {
      if (resolveDuration(audio) > 0) {
        audio.removeEventListener('durationchange', onDurationReady)
        audio.removeEventListener('timeupdate', onDurationReady)
        finishProbe()
      }
    }

    audio.addEventListener('durationchange', onDurationReady)
    audio.addEventListener('timeupdate', onDurationReady)
    audio.currentTime = 1e10

    window.setTimeout(() => {
      audio.removeEventListener('durationchange', onDurationReady)
      audio.removeEventListener('timeupdate', onDurationReady)
      if (probingDurationRef.current) {
        finishProbe()
      }
    }, 500)
  }

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume
    }
  }, [volume])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return undefined

    probingDurationRef.current = false
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setIsRepeat(false)
    audio.loop = false

    const handleLoaded = () => {
      syncDuration(audio)
      if (!resolveDuration(audio)) {
        probeDuration(audio)
      }
    }
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
      syncDuration(audio)
    }
    const handleEnded = () => {
      setIsPlaying(false)
      if (!audio.loop) {
        setCurrentTime(0)
      }
    }

    audio.addEventListener('loadedmetadata', handleLoaded)
    audio.addEventListener('durationchange', handleLoaded)
    audio.addEventListener('canplay', handleLoaded)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleAudioError)

    if (!audioUrl) {
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
      setAudioError('Audio introuvable')
      return () => {
        audio.removeEventListener('loadedmetadata', handleLoaded)
        audio.removeEventListener('durationchange', handleLoaded)
        audio.removeEventListener('canplay', handleLoaded)
        audio.removeEventListener('timeupdate', handleTimeUpdate)
        audio.removeEventListener('ended', handleEnded)
        audio.removeEventListener('error', handleAudioError)
      }
    }

    setAudioError(null)
    audio.pause()
    audio.src = audioUrl
    audio.load()

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoaded)
      audio.removeEventListener('durationchange', handleLoaded)
      audio.removeEventListener('canplay', handleLoaded)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleAudioError)
    }
  }, [selectedCantique?.id, audioUrl])

  if (!cantiqueHasAudio || !selectedCantique) {
    return null
  }

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return '0:00'
    const minutes = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`
  }

  const handleAudioError = () => {
    setAudioError('Impossible de charger l’audio')
    setIsPlaying(false)
  }

  const handlePlayPause = async () => {
    if (!audioRef.current || !audioUrl) return

    try {
      if (isPlaying) {
        audioRef.current.pause()
        setIsPlaying(false)
        return
      }

      const audio = audioRef.current
      syncDuration(audio)
      if (!resolveDuration(audio)) {
        probeDuration(audio)
      }

      if (audio.readyState >= 2) {
        await audio.play()
        setIsPlaying(true)
        syncDuration(audio)
        return
      }

      await new Promise((resolve, reject) => {
        const cleanup = () => {
          audio.removeEventListener('canplay', onCanPlay)
          audio.removeEventListener('loadedmetadata', onCanPlay)
          audio.removeEventListener('error', onError)
        }

        const onCanPlay = async () => {
          cleanup()
          try {
            await audio.play()
            setIsPlaying(true)
            syncDuration(audio)
            resolve()
          } catch (error) {
            reject(error)
          }
        }

        const onError = () => {
          cleanup()
          reject(new Error('Impossible de charger l’audio'))
        }

        audio.addEventListener('canplay', onCanPlay, { once: true })
        audio.addEventListener('loadedmetadata', onCanPlay, { once: true })
        audio.addEventListener('error', onError, { once: true })
      })
    } catch (error) {
      console.error('Erreur lecture audio:', error)
      setIsPlaying(false)
    }
  }

  const handleSeek = (event) => {
    const nextTime = Number(event.target.value)
    const effectiveDuration = duration || resolveDuration(audioRef.current)
    if (!effectiveDuration) return
    if (audioRef.current) {
      audioRef.current.currentTime = nextTime
    }
    setCurrentTime(nextTime)
  }

  const handleVolumeChange = (event) => {
    setVolume(Number(event.target.value))
  }

  const handleRepeatToggle = () => {
    setIsRepeat((prev) => {
      const nextValue = !prev
      if (audioRef.current) {
        audioRef.current.loop = nextValue
      }
      return nextValue
    })
  }

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    setIsPlaying(false)
    setCurrentTime(0)
    onClose?.()
  }

  const handleShare = async () => {
    if (!audioUrl) {
      alert('Aucun audio disponible à partager')
      return
    }

    const title = selectedCantique.title || 'Audio'

    // Try to fetch the audio and share as a named file when supported
    try {
      const resp = await fetch(audioUrl)
      const blob = await resp.blob()

      // determine extension
      const mime = blob.type || resp.headers.get('content-type') || ''
      const mimeToExt = (m) => {
        if (!m) return 'mp3'
        if (m.includes('mpeg')) return 'mp3'
        if (m.includes('wav')) return 'wav'
        if (m.includes('webm')) return 'webm'
        if (m.includes('ogg')) return 'ogg'
        if (m.includes('mp4') || m.includes('m4a')) return 'm4a'
        return 'mp3'
      }
      const ext = mimeToExt(mime)

      const sanitize = (s) => String(s || '').replace(/[^a-z0-9\-_. ]+/ig, '').replace(/\s+/g, ' ').trim().slice(0, 200) || 'audio'
      const filename = `${sanitize(title)}.${ext}`

      const file = new File([blob], filename, { type: mime || 'audio/mpeg' })

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title,
            text: `Écoute : ${title}`,
          })
          return
        } catch (err) {
          console.error('Partage fichier annulé', err)
        }
      }

      // Fallback: copy text+url to clipboard
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(`Écoute : ${title}\n${audioUrl}`)
          alert('Lien de partage copié dans le presse-papiers')
          return
        } catch (error) {
          console.error('Impossible de copier dans le presse-papiers', error)
        }
      }

      alert(`Audio lié au cantique « ${title} » : ${audioUrl}`)
    } catch (err) {
      console.error('Erreur lors de la préparation du partage :', err)
      // fallback to simple share text
      try {
        if (navigator.share) {
          await navigator.share({ title: selectedCantique.title, text: `Écoute : ${selectedCantique.title}`, url: audioUrl })
          return
        }
      } catch (e) {
        // ignore
      }
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(`Écoute : ${selectedCantique.title}\n${audioUrl}`)
          alert('Lien de partage copié dans le presse-papiers')
          return
        } catch (error) {
          console.error('Impossible de copier dans le presse-papiers', error)
        }
      }
      alert(`Audio lié au cantique « ${selectedCantique.title} »`)
    }
  }

  const songLabel = selectedCantique.number
    ? `${selectedCantique.number} • ${selectedCantique.title}`
    : selectedCantique.title

  const effectiveDuration = duration || resolveDuration(audioRef.current)
  const progressPercent = effectiveDuration > 0
    ? Math.min(100, (currentTime / effectiveDuration) * 100)
    : 0

  return (
    <div className="lector">
      <audio ref={audioRef} preload="metadata" />
      <div className="lector__container">
        <div className="lector__main">
          <div className="lector__song__info">
            <h3 className="lector__song__title">{selectedCantique.title}</h3>
            <p className="lector__song__artist">{songLabel}</p>
          </div>

          <button className="lector__action__btn" title="Fermer le lecteur" onClick={handleClose} type="button">
            <X size={18} />
          </button>

          <div className="lector__controls__center">
            <button className="lector__control__btn" title="Précédent" type="button" disabled>
              <SkipBack size={18} />
            </button>
            <button
              className="lector__control__btn lector__control__btn--play"
              onClick={handlePlayPause}
              title={isPlaying ? 'Pause' : 'Lecture'}
              type="button"
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} fill="white" />}
            </button>
            <button className="lector__control__btn" title="Suivant" type="button" disabled>
              <SkipForward size={18} />
            </button>
            <button className="lector__control__btn" title="Répéter" onClick={handleRepeatToggle} type="button">
              <Repeat2 size={18} className={isRepeat ? 'lector__icon--active' : ''} />
            </button>
          </div>

          <div className="lector__controls__right">
            <div className="lector__volume__control">
              <div className="lector__volume__icon">
                <Volume2 size={16} />
              </div>
              <div className="lector__volume__bar">
                <div className="lector__volume__fill" style={{ width: `${volume * 100}%` }} />
                <input
                  className="lector__volume__input"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  aria-label="Contrôle du volume"
                />
              </div>
            </div>

            <div className="lector__actions__right">
              <button
                className="lector__action__btn"
                title="Partager"
                onClick={handleShare}
                type="button"
              >
                <Share2 size={18} />
              </button>
            </div>
          </div>
        </div>

        <div className="lector__progress__section">
          <div className="lector__time__current">{formatTime(currentTime)}</div>
          <div className="lector__progress__bar">
            <div
              className="lector__progress__fill"
              style={{ width: `${progressPercent}%` }}
            />
            <input
              className="lector__progress__input"
              type="range"
              min="0"
              max={effectiveDuration || 100}
              step="0.1"
              value={effectiveDuration ? currentTime : 0}
              onChange={handleSeek}
              disabled={!effectiveDuration}
              aria-label="Barre de progression"
            />
          </div>
          <div className="lector__time__total">{formatTime(effectiveDuration)}</div>
        </div>
        {!online && !isPlaying ? (
          <div className="lector__error-message" role="status">
            Hors connexion : l’audio n’est pas disponible. Les paroles restent lisibles.
          </div>
        ) : audioError && (
          <div className="lector__error-message" role="alert">
            {audioError}
          </div>
        )}
      </div>
    </div>
  )
}

export default Lector
